/**
 * MyDayBriefingModal.js
 *
 * Presentational "Mai napom" (My day) briefing as a premium FLOATING card (not full screen; blueprint
 * docs/PREMIUM_MODAL_BLUEPRINT.md): transparent Modal over a solid dim backdrop, radius-28 card (max height 88%, max
 * width 520), navy gradient header with a Lottie hero + greeting + date + close, then the Munkanap hero card (start / live
 * elapsed + stop — the briefing HOSTS the start-of-day prompt), metric tiles, the prioritised "Fókusz" block (ranked list,
 * AI day plan, AI reply drafts, leads / projects / dormant customers; see `MyDayFocus`), today's follow-ups, calendar events, other
 * work items and a compact upcoming list, and a sticky footer with the choices (Értesítsen újra / Következő induláskor /
 * Kihagyás ma) and the primary CTA. Opaque surfaces (no glass), spring slide / fade-up entrance, dark mode via
 * `useManagementTheme`, Android back = close. All behaviour arrives via props; see `MyDayBriefing` for the logic.
 *
 * @module components/management/MyDayBriefingModal
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ActivityIndicator, Animated, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import LottieView from 'lottie-react-native';
import {useTranslation} from 'react-i18next';
import Icon from './SafeIcon';
import {MetricTile, QuietEmpty, SectionTitle} from './FollowUpAgendaKit';
import {WorkdayHeroCard} from '../workday/WorkdayCards';
import {EventRow} from './FollowUpRows';
import MyDayFocus from './MyDayFocus';
import {useManagementTheme, withAlpha, BRAND} from '../../helpers/managementTheme';
import {useBottomInset, useTopInset} from '../../helpers/safeArea';
import {entityOf, friendlyDay, greetingKey, nonEmptyDays, plainText} from '../../helpers/salesFollowUpApi';
import {SNOOZE_LABEL_KEYS, SNOOZE_MINUTES} from '../../helpers/myDayBriefing';
import {buildDayTimeline, dayTotals, workItemRoute, workKindMeta} from '../../helpers/myDayWorkItems';
import {FOLLOW_UP_ICONS, followUpTypeLabel} from '../../config/leadContactMeta';

const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '');
const UPCOMING_PREVIEW = 5;

/** Footer button (outlined). */
const FooterBtn = ({icon, label, onPress, tone}) => {
    const m = useManagementTheme();

    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={label}
            style={({pressed}) => [styles.footBtn, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, pressed && styles.pressed]}
        >
            <Icon name={icon} size={18} color={tone || m.colors.primary} />
            <Text style={[styles.footBtnText, {color: m.colors.heading}]} numberOfLines={2}>{label}</Text>
        </Pressable>
    );
};

/** One compact line (follow-up or work item). */
const BriefRow = ({icon, title, subtitle, time, overdueLabel, badges, onPress}) => {
    const m = useManagementTheme();
    const tone = overdueLabel ? m.colors.danger : m.colors.primary;

    return (
        <Pressable
            disabled={!onPress}
            onPress={onPress}
            accessibilityRole={onPress ? 'button' : undefined}
            style={({pressed}) => [styles.row, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1), pressed && styles.pressed]}
        >
            <View style={[styles.rowIcon, {backgroundColor: withAlpha(tone, m.isDark ? 0.2 : 0.12)}]}>
                <Icon name={icon} size={20} color={tone} />
            </View>
            <View style={{flex: 1}}>
                <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                {!!subtitle && <Text style={[styles.rowSub, {color: m.colors.subText}]} numberOfLines={1}>{subtitle}</Text>}
                <View style={styles.rowMeta}>
                    {!!time && <Text style={[styles.rowTime, {color: m.colors.subText}]}>{time}</Text>}
                    {(badges || []).map((b) => {
                        const c = b.tone === 'danger' ? m.colors.danger : b.tone;

                        return (
                            <View key={b.label} style={[styles.overdueBadge, {backgroundColor: withAlpha(c, m.isDark ? 0.22 : 0.12)}]}>
                                <Text style={[styles.overdueText, {color: c}]}>{b.label}</Text>
                            </View>
                        );
                    })}
                    {!!overdueLabel && (
                        <View style={[styles.overdueBadge, {backgroundColor: withAlpha(m.colors.danger, m.isDark ? 0.22 : 0.12)}]}>
                            <Text style={[styles.overdueText, {color: m.colors.danger}]}>{overdueLabel}</Text>
                        </View>
                    )}
                </View>
            </View>
            {!!onPress && <Icon name="chevron-right" size={20} color={m.colors.muted} />}
        </Pressable>
    );
};

/** Waiting time label for a mail work item ("5h" under 48h, days after). */
const waitingLabel = (t, hours) => {
    const h = Math.round(Number(hours) || 0);

    if (h <= 0) {
        return '';
    }

    return h >= 48
        ? t('myday_mail_waiting_days', {count: Math.floor(h / 24), defaultValue: '{{count}} days waiting'})
        : t('myday_mail_waiting_hours', {count: h, defaultValue: '{{count}}h waiting'});
};

/**
 * Work item row (kind icon tile, readable subtitle, time or "all day", overdue badge, tap when a screen is mapped).
 * @param {{item: object, onOpen?: (item: object) => void}} props props.
 * @returns {JSX.Element} row.
 */
export const WorkItemRow = ({item, onOpen}) => {
    const {t} = useTranslation();
    const meta = workKindMeta(item.kind);
    const mapped = !!workItemRoute(item);
    const sub = plainText(item.subtitle) || plainText(item.entity?.name);
    const isMail = item.kind === 'mail';
    const mail = isMail ? item.mail : null;
    const badges = [];

    if (mail?.unanswered) {
        badges.push({label: t('myday_mail_unanswered', {defaultValue: 'Unanswered'}), tone: 'danger'});
    }

    if (mail?.important) {
        badges.push({label: t('myday_mail_important', {defaultValue: 'Important'}), tone: BRAND.base});
    }

    const waiting = mail ? waitingLabel(t, mail.hoursWaiting) : '';

    return (
        <BriefRow
            icon={meta.icon}
            title={plainText(item.title) || (isMail ? t('myday_mail_no_subject', {defaultValue: '(no subject)'}) : t(meta.labelKey))}
            subtitle={isMail ? [sub, waiting].filter(Boolean).join(' · ') : [t(meta.labelKey), sub].filter(Boolean).join(' · ')}
            badges={badges}
            time={item.allDay ? t('sfu_all_day') : fmtTime(item.dueAt)}
            overdueLabel={item.isOverdue ? t('myday_overdue') : ''}
            onPress={mapped && onOpen ? () => onOpen(item) : undefined}
        />
    );
};

/** Title / icon of a timeline entry (used by the "next up" card). */
const entryHead = (t, entry) => {
    const it = entry.item;

    if (entry.type === 'followUp') {
        return {icon: FOLLOW_UP_ICONS[it.type] || 'dots-horizontal', title: plainText(entityOf(it).name) || plainText(it.title) || '—'};
    }

    if (entry.type === 'event') {
        return {icon: 'calendar-blank-outline', title: plainText(it.title) || '—'};
    }

    return {icon: workKindMeta(it.kind).icon, title: plainText(it.title) || t(workKindMeta(it.kind).labelKey)};
};

/** "Now" / "in 25 min" / "in 2 h" for a start time. */
const startsInLabel = (t, at, endAt, now) => {
    const min = Math.round((at - now) / 60000);

    if (min <= 0) {
        return endAt !== null && endAt > now ? t('myday_now') : '';
    }

    return min < 60 ? t('myday_starts_in_min', {count: min}) : t('myday_starts_in_hours', {count: Math.round(min / 60)});
};

/** Highlight of the very next timed thing on the plan. */
const NextUpCard = ({entry, onOpenWorkItem}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const head = entryHead(t, entry);
    const rel = startsInLabel(t, entry.at, entry.endAt, Date.now());
    const mapped = entry.type === 'work' && !!workItemRoute(entry.item) && !!onOpenWorkItem;

    return (
        <Pressable
            disabled={!mapped}
            onPress={mapped ? () => onOpenWorkItem(entry.item) : undefined}
            accessibilityRole={mapped ? 'button' : undefined}
            style={({pressed}) => [styles.nextCard, {backgroundColor: withAlpha(BRAND.base, m.isDark ? 0.18 : 0.1), borderColor: withAlpha(BRAND.base, 0.45)}, pressed && styles.pressed]}
        >
            <View style={[styles.rowIcon, {backgroundColor: BRAND.base}]}>
                <Icon name={head.icon} size={20} color="#FFFFFF" />
            </View>
            <View style={{flex: 1}}>
                <Text style={[styles.nextKicker, {color: BRAND.base}]}>{t('myday_next_up').toUpperCase()}</Text>
                <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={2}>{head.title}</Text>
                <Text style={[styles.rowSub, {color: m.colors.subText}]}>{[fmtTime(entry.at), rel].filter(Boolean).join(' · ')}</Text>
            </View>
        </Pressable>
    );
};

/** One entry of the day plan, rendered with the row matching its source. */
const TimelineEntry = ({entry, onOpenWorkItem}) => {
    const {t} = useTranslation();
    const it = entry.item;

    if (entry.type === 'event') {
        return <EventRow event={it} />;
    }

    if (entry.type === 'work') {
        return <WorkItemRow item={it} onOpen={onOpenWorkItem} />;
    }

    return (
        <BriefRow
            icon={FOLLOW_UP_ICONS[it.type] || 'dots-horizontal'}
            title={plainText(entityOf(it).name) || plainText(it.title) || '—'}
            subtitle={[followUpTypeLabel(t, it.type), plainText(entityOf(it).company)].filter(Boolean).join(' · ')}
            time={fmtTime(it.dueAt)}
            overdueLabel={entry.overdue ? t('myday_overdue') : ''}
        />
    );
};

/**
 * Floating briefing card.
 * @param {object} props props.
 * @param {boolean} props.visible whether shown.
 * @param {'loading'|'ready'|'error'} props.phase data state.
 * @param {object} props.agenda normalized agenda.
 * @param {object} [props.focus] normalized focus block (projects / leads / dormant customers; empty on old servers).
 * @param {string} props.error error text (phase error).
 * @param {boolean} props.canOpenAgenda whether the primary CTA may open the Teendők screen.
 * @param {() => void} props.onClose X / back / "Rendben".
 * @param {() => void} props.onRetry reload.
 * @param {() => void} props.onOpenAgenda primary CTA.
 * @param {(minutes: number) => void} props.onSnooze chosen snooze.
 * @param {() => void} props.onNextLaunch "Következő induláskor".
 * @param {() => void} props.onSkipToday "Kihagyás ma".
 * @param {(item: object) => void} props.onOpenWorkItem tap on a mapped work item.
 * @param {(target: {name: string, params: object}) => void} [props.onNavigate] close the briefing and open a screen (focus rows, AI reply draft).
 * @returns {JSX.Element} modal.
 */
export default function MyDayBriefingModal({
    visible, phase, agenda, focus, error, canOpenAgenda, onClose, onRetry, onOpenAgenda, onSnooze, onNextLaunch, onSkipToday, onOpenWorkItem, onNavigate,
}) {
    const {t, i18n} = useTranslation();
    const m = useManagementTheme();
    const top = useTopInset();
    const bottom = useBottomInset();
    const [picking, setPicking] = useState(false);
    const fu = agenda.access.followUps;
    const totals = dayTotals(agenda);
    const events = agenda.calendarEvents;
    const timeline = useMemo(() => buildDayTimeline(agenda), [agenda]);
    const days = useMemo(() => nonEmptyDays(agenda.upcoming.byDay), [agenda.upcoming.byDay]);
    const upcomingWork = agenda.workItems.upcoming;
    const upcomingCount = (fu ? days.reduce((n, d) => n + d.followUps.length + d.calendarEvents.length, 0) : 0) + upcomingWork.length;
    const empty = timeline.overdue.length === 0 && timeline.anytime.length === 0 && timeline.timed.length === 0;

    let dateLine = '';

    try {
        dateLine = new Date().toLocaleDateString(i18n?.language || undefined, {weekday: 'long', month: 'long', day: 'numeric'});
    } catch (_e) {
        dateLine = new Date().toLocaleDateString(undefined, {weekday: 'long', month: 'long', day: 'numeric'});
    }

    const close = () => { setPicking(false); onClose(); };
    const pick = (minutes) => { setPicking(false); onSnooze(minutes); };

    const body = () => {
        if (phase === 'loading') {
            return <View style={styles.center}><ActivityIndicator size="large" color={BRAND.base} /></View>;
        }

        if (phase === 'error') {
            return (
                <>
                    <QuietEmpty icon="cloud-alert" tone="primary" title={t('myday_error_title')} text={error || t('myday_error_text')} />
                    <Pressable onPress={onRetry} accessibilityRole="button" style={({pressed}) => [styles.retry, {borderColor: BRAND.base}, pressed && styles.pressed]}>
                        <Icon name="refresh" size={16} color={BRAND.base} />
                        <Text style={[styles.retryText, {color: BRAND.base}]}>{t('myday_retry')}</Text>
                    </Pressable>
                </>
            );
        }

        return (
            <>
                <View style={styles.tiles}>
                    <MetricTile icon="alert-circle-outline" label={t('sfu_metric_overdue')} value={totals.overdue} accent={m.colors.danger} />
                    <MetricTile icon="calendar-today" label={t('sfu_metric_today')} value={totals.today} accent={BRAND.base} />
                    <MetricTile icon="calendar-blank-outline" label={t('myday_metric_events')} value={agenda.counts.calendarToday || events.length} accent={m.colors.info} />
                </View>

                {onNavigate && <MyDayFocus agenda={agenda} focus={focus} onNavigate={onNavigate} />}

                {empty ? (
                    <QuietEmpty icon="party-popper" tone="success" title={t('myday_empty_title')} text={t('myday_empty_text')} />
                ) : (
                    <>
                        {timeline.next && <NextUpCard entry={timeline.next} onOpenWorkItem={onOpenWorkItem} />}
                        {timeline.overdue.length > 0 && (
                            <>
                                <SectionTitle icon="alert-circle-outline" title={t('myday_section_overdue')} count={timeline.overdue.length} />
                                {timeline.overdue.map((e) => <TimelineEntry key={e.key} entry={e} onOpenWorkItem={onOpenWorkItem} />)}
                            </>
                        )}
                        {timeline.timed.length > 0 && (
                            <>
                                <SectionTitle icon="clock-outline" title={t('myday_section_schedule')} count={timeline.timed.length} />
                                {timeline.timed.map((e) => <TimelineEntry key={e.key} entry={e} onOpenWorkItem={onOpenWorkItem} />)}
                            </>
                        )}
                        {timeline.anytime.length > 0 && (
                            <>
                                <SectionTitle icon="clipboard-list-outline" title={t('myday_section_anytime')} count={timeline.anytime.length} />
                                {timeline.anytime.map((e) => <TimelineEntry key={e.key} entry={e} onOpenWorkItem={onOpenWorkItem} />)}
                            </>
                        )}
                    </>
                )}

                {upcomingCount > 0 && (
                    <>
                        <SectionTitle icon="calendar-arrow-right" title={t('myday_upcoming')} count={upcomingCount} />
                        <View style={[styles.upcomingBox, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
                            {[
                                ...(fu ? days.flatMap((d) => {
                                    const day = friendlyDay(d.date, new Date(), i18n?.language);

                                    return [
                                        ...d.calendarEvents.map((ev) => ({key: `e${ev._id}`, icon: 'calendar-blank-outline', label: day.label, text: plainText(ev.title)})),
                                        ...d.followUps.map((f) => ({key: `f${f._id}`, icon: FOLLOW_UP_ICONS[f.type] || 'dots-horizontal', label: day.label, text: plainText(entityOf(f).name) || plainText(f.title)})),
                                    ];
                                }) : []),
                                ...upcomingWork.map((w) => ({
                                    key: `w${w.kind}${w.id}`,
                                    icon: workKindMeta(w.kind).icon,
                                    label: w.dueAt ? new Date(w.dueAt).toLocaleDateString(i18n?.language || undefined, {month: 'short', day: 'numeric'}) : '',
                                    text: plainText(w.title),
                                })),
                            ].slice(0, UPCOMING_PREVIEW).map((u) => (
                                <View key={u.key} style={styles.upRow}>
                                    <Icon name={u.icon} size={15} color={m.colors.muted} />
                                    <Text style={[styles.upText, {color: m.colors.text}]} numberOfLines={1}>{u.text || '—'}</Text>
                                    <Text style={[styles.upDay, {color: m.colors.subText}]} numberOfLines={1}>{u.label}</Text>
                                </View>
                            ))}
                        </View>
                    </>
                )}
            </>
        );
    };

    const {height: winH, width: winW} = useWindowDimensions();
    const enter = useRef(new Animated.Value(0)).current;
    const wide = winW >= 700;

    useEffect(() => {
        if (visible) {
            enter.setValue(0);
            Animated.spring(enter, {toValue: 1, friction: 9, tension: 70, useNativeDriver: true}).start();
        }
    }, [visible, enter]);

    const cardAnim = {
        opacity: enter,
        transform: [{translateY: enter.interpolate({inputRange: [0, 1], outputRange: [36, 0]})}, {scale: enter.interpolate({inputRange: [0, 1], outputRange: [0.97, 1]})}],
    };

    return (
        <Modal
            visible={visible}
            animationType="fade"
            transparent
            statusBarTranslucent={Platform.OS === 'android'}
            onRequestClose={close}
        >
            <View style={[styles.backdrop, {justifyContent: wide ? 'center' : 'flex-end', paddingTop: top + 8, paddingBottom: Math.max(bottom, 12) + 6}]}>
                <Pressable style={StyleSheet.absoluteFill} onPress={close} accessibilityRole="button" accessibilityLabel={t('myday_close')} />
                <Animated.View style={[styles.card, {backgroundColor: m.colors.bg, borderColor: m.colors.border, maxHeight: Math.round(winH * 0.88)}, cardAnim]}>
                    <LinearGradient colors={['#1f2a44', '#243656']} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.header}>
                        <View style={styles.accentBar} />
                        <View style={styles.heroLottie}>
                            <LottieView source={require('../../../assets/animations/workday-clock.json')} autoPlay loop style={styles.heroLottieView} />
                        </View>
                        <View style={styles.headerText}>
                            <Text style={styles.kicker} numberOfLines={1}>{t('myday_kicker').toUpperCase()}</Text>
                            <Text style={styles.headerTitle} numberOfLines={1}>{t('myday_title')}</Text>
                            <Text style={styles.headerSub} numberOfLines={2}>{`${t(`sfu_greet_${greetingKey()}`)} · ${dateLine}`}</Text>
                        </View>
                        <Pressable
                            onPress={close}
                            accessibilityRole="button"
                            accessibilityLabel={t('myday_close')}
                            hitSlop={10}
                            style={({pressed}) => [styles.closeBtn, pressed && styles.pressed]}
                        >
                            <Icon name="close" size={20} color="#FFFFFF" />
                        </Pressable>
                    </LinearGradient>

                    <ScrollView style={styles.scroll} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
                        {/* Munkanap: the briefing hosts the start-of-day prompt (renders nothing while unsupported). */}
                        <WorkdayHeroCard />
                        {body()}
                    </ScrollView>

                    <View style={[styles.footer, {backgroundColor: m.colors.surface, borderTopColor: m.colors.border}]}>
                        {picking ? (
                            <>
                                <Text style={[styles.pickTitle, {color: m.colors.heading}]}>{t('myday_snooze_title')}</Text>
                                <View style={styles.footRow}>
                                    {SNOOZE_MINUTES.map((min) => (
                                        <FooterBtn key={min} icon="bell-ring-outline" label={t(SNOOZE_LABEL_KEYS[min])} onPress={() => pick(min)} />
                                    ))}
                                </View>
                                <Pressable onPress={() => setPicking(false)} accessibilityRole="button" style={styles.cancelLink}>
                                    <Text style={[styles.cancelText, {color: m.colors.subText}]}>{t('myday_cancel')}</Text>
                                </Pressable>
                            </>
                        ) : (
                            <>
                                <Pressable
                                    onPress={canOpenAgenda ? onOpenAgenda : close}
                                    accessibilityRole="button"
                                    style={({pressed}) => [styles.cta, {backgroundColor: BRAND.base}, pressed && styles.pressed]}
                                >
                                    <Text style={styles.ctaText}>{canOpenAgenda ? t('myday_open') : t('myday_ok')}</Text>
                                </Pressable>
                                <View style={styles.footRow}>
                                    <FooterBtn icon="bell-ring-outline" label={t('myday_remind_again')} onPress={() => setPicking(true)} />
                                    <FooterBtn icon="rocket-launch-outline" label={t('myday_next_launch')} onPress={onNextLaunch} />
                                    <FooterBtn icon="eye-off-outline" label={t('myday_skip_today')} onPress={onSkipToday} tone={m.colors.subText} />
                                </View>
                            </>
                        )}
                    </View>
                </Animated.View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {flex: 1, alignItems: 'center', paddingHorizontal: 12, backgroundColor: 'rgba(8,12,24,0.58)'},
    card: {width: '100%', maxWidth: 520, borderRadius: 28, borderWidth: 1, overflow: 'hidden'},
    header: {flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingTop: 18, paddingBottom: 16},
    accentBar: {position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: '#F59E0B'},
    heroLottie: {width: 56, height: 56, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.12)', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'},
    heroLottieView: {width: 50, height: 50},
    headerText: {flex: 1},
    kicker: {color: 'rgba(226,236,255,0.72)', fontSize: 10.5, fontWeight: '800', letterSpacing: 1.1},
    headerTitle: {color: '#FFFFFF', fontSize: 22, fontWeight: '900', letterSpacing: -0.4},
    headerSub: {color: 'rgba(226,236,255,0.82)', fontSize: 12.5, fontWeight: '600', marginTop: 1},
    closeBtn: {width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.14)', alignItems: 'center', justifyContent: 'center'},
    scroll: {flexGrow: 0, flexShrink: 1},
    content: {paddingHorizontal: 16, paddingTop: 16, paddingBottom: 20},
    center: {paddingVertical: 48, alignItems: 'center', justifyContent: 'center'},
    tiles: {flexDirection: 'row', gap: 10, marginBottom: 6},
    nextCard: {flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 20, borderWidth: 1.4, padding: 12, marginTop: 10, marginBottom: 4},
    nextKicker: {fontSize: 10.5, fontWeight: '900', letterSpacing: 1},
    row: {flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 20, borderWidth: 1, padding: 12, marginBottom: 10},
    rowIcon: {width: 40, height: 40, borderRadius: 14, alignItems: 'center', justifyContent: 'center'},
    rowTitle: {fontSize: 15, fontWeight: '800'},
    rowSub: {fontSize: 12.5, fontWeight: '700', marginTop: 1},
    rowMeta: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 4},
    rowTime: {fontSize: 12.5, fontWeight: '800'},
    overdueBadge: {borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2},
    overdueText: {fontSize: 11, fontWeight: '900'},
    upcomingBox: {borderRadius: 20, borderWidth: 1, paddingVertical: 8, paddingHorizontal: 12},
    upRow: {flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6},
    upText: {flex: 1, fontSize: 13.5, fontWeight: '700'},
    upDay: {fontSize: 12, fontWeight: '700', maxWidth: 110},
    footer: {borderTopWidth: 1, paddingHorizontal: 16, paddingTop: 12, paddingBottom: 14, gap: 10},
    cta: {borderRadius: 16, paddingVertical: 14, alignItems: 'center'},
    ctaText: {color: '#FFFFFF', fontSize: 16, fontWeight: '900'},
    footRow: {flexDirection: 'row', gap: 8},
    footBtn: {flex: 1, minHeight: 56, borderRadius: 16, borderWidth: 1, paddingVertical: 8, paddingHorizontal: 6, alignItems: 'center', justifyContent: 'center', gap: 4},
    footBtnText: {fontSize: 12, fontWeight: '800', textAlign: 'center'},
    pickTitle: {fontSize: 15, fontWeight: '900', textAlign: 'center'},
    cancelLink: {alignSelf: 'center', paddingVertical: 6, paddingHorizontal: 16},
    cancelText: {fontSize: 13.5, fontWeight: '800'},
    retry: {flexDirection: 'row', alignItems: 'center', alignSelf: 'center', gap: 6, borderWidth: 1.4, borderRadius: 999, paddingHorizontal: 16, paddingVertical: 9, marginTop: 12},
    retryText: {fontSize: 13.5, fontWeight: '800'},
    pressed: {opacity: 0.72, transform: [{scale: 0.98}]},
});
